import { themeVariables } from "../../constants/tokens.js";
import { shadows } from "../../constants/shadows.js";
const SWITCH_CLOSE_ICON = "url(data:image/svg+xml,%3Csvg%20width%3D%227%22%20height%3D%227%22%20viewBox%3D%220%200%207%207%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M0.7%207L0%206.3L2.8%203.5L0%200.7L0.7%20-4.76837e-07L3.5%202.8L6.3%20-4.76837e-07L7%200.7L4.2%203.5L7%206.3L6.3%207L3.5%204.2L0.7%207Z%22%20fill%3D%22white%22%2F%3E%3C%2Fsvg%3E)";
const SWITCH_CHECK_ICON = "url(data:image/svg+xml,%3Csvg%20width%3D%229%22%20height%3D%226%22%20viewBox%3D%220%200%209%206%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M2.84703%206.00586L0%203.15882L0.711759%202.44707L2.84703%204.58234L7.42976%20-0.000384331L8.14152%200.711374L2.84703%206.00586Z%22%20fill%3D%22%23131210%22%2F%3E%3C%2Fsvg%3E)";
const InteractionVariables = {
  base: {
    borderColor: themeVariables.color.border.tertiary
  },
  active: {
    borderColor: themeVariables.color.border.primary,
    color: themeVariables.color.text.primary
  },
  error: {
    borderColor: themeVariables.color.border.error,
    backgroundColor: themeVariables.color.surface["error-tertiary"]
  },
  disabled: {
    borderColor: themeVariables.color.border.tertiary,
    backgroundColor: themeVariables.color.surface.disabled,
    color: themeVariables.color.text.disabled
  },
  focus: {
    borderColor: themeVariables.color.border.focus
  }
};
const inputs = {
  /**
   * While its input is focused, a label takes `palette.primary.main`, which is white and so
   * disappears into the outline. Hold the resting colour instead. Error labels are left out
   * so they stay red, which is the order MUI itself applies.
   */
  MuiFormLabel: {
    styleOverrides: {
      root: {
        ["&.Mui-focused:not(.Mui-error)"]: {
          color: themeVariables.color.text.secondary
        }
      }
    }
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        display: "block",
        flex: 1,
        color: themeVariables.color.text.secondary,
        fontSize: themeVariables.fonts.size.xs,
        fontFamily: themeVariables.fonts.family.body,
        fontWeight: themeVariables.fonts.weight.medium,
        lineHeight: themeVariables.fonts.lineHeight["leading-4"],
        letterSpacing: themeVariables.fonts.letterSpacing.wider
      }
    }
  },
  /**
   * Input base component
   * it's used to show selected values in TextField, Select, date pickers, etc
   */
  MuiInputBase: {
    styleOverrides: {
      root: {
        // ['&.MuiSelect-root']: {
        // height: themeVariables.fonts.lineHeight['leading-4'],
        // },
        ["&.Mui-focused"]: {
          ...InteractionVariables.focus
        }
      },
      input: {
        background: themeVariables.color.surface.primary,
        borderRadius: themeVariables.sizes.radius.md,
        color: themeVariables.color.text.primary,
        fontFamily: themeVariables.typography.fontFamily,
        fontSize: themeVariables.fonts.size.sm,
        fontStyle: "normal",
        fontWeight: themeVariables.fonts.weight.medium,
        letterSpacing: themeVariables.fonts.letterSpacing.wide,
        lineHeight: themeVariables.fonts.lineHeight["leading-5"],
        ["&::placeholder"]: {
          color: themeVariables.color.text.tertiary,
          opacity: 1
        }
      }
    }
  },
  /**
   * Standard variant inputs, used by TextInput, InputSelect and the filter dropdowns.
   * MUI draws a bottom line on them with a hover and focus animation. Every input in the
   * design system draws its own border, so the line is dropped everywhere.
   */
  MuiInput: {
    defaultProps: {
      disableUnderline: true
    }
  },
  /**
   * Outlined input component
   * it's used to show selected values in TextField, Select, date pickers, etc
   */
  MuiTextField: {
    styleOverrides: {
      root: {
        borderRadius: themeVariables.sizes.radius.md,
        border: `1px solid ${themeVariables.color.border.tertiary}`,
        background: themeVariables.color.surface.primary,
        lineHeight: themeVariables.fonts.lineHeight["leading-10"],
        padding: themeVariables.sizes.spacing.none,
        margin: themeVariables.sizes.spacing.none,
        ["& .MuiInputBase-input"]: {
          overflow: "hidden",
          color: themeVariables.color.text.primary,
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontFamily: themeVariables.fonts.family.body,
          fontSize: themeVariables.fonts.size.sm,
          fontStyle: "normal",
          fontWeight: themeVariables.fonts.weight.normal,
          lineHeight: themeVariables.fonts.lineHeight["leading-5"],
          letterSpacing: themeVariables.fonts.letterSpacing.wide,
          padding: themeVariables.sizes.spacing["06"]
        },
        ["& .MuiInputBase-inputMultiline"]: {
          overflow: "auto",
          textOverflow: "clip",
          whiteSpace: "pre-wrap"
        },
        ["&.MuiFormControl-root.MuiTextField-root"]: {
          border: "none",
          ["&.hasErrors"]: {
            border: "1px solid red"
          }
        }
      }
    }
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        "&.Mui-checked": {
          color: themeVariables.color.surface.brand
        },
        "&.Mui-disabled": {
          color: themeVariables.color.border.tertiary
        }
      }
    }
  },
  /**
   * MUI colours a checked radio with `palette.primary.main`, which is white and so vanishes.
   * Matches the checkbox above, including the disabled rule: it has to come last so a
   * disabled checked radio reads as disabled rather than brand.
   */
  MuiRadio: {
    styleOverrides: {
      root: {
        "&.Mui-checked": {
          color: themeVariables.color.surface.brand
        },
        "&.Mui-disabled": {
          color: themeVariables.color.border.tertiary
        }
      }
    }
  },
  /**
   * The rail, track and thumb are all `currentColor`, and MUI sets the root's colour from
   * `palette[color].main` — white by default, which blanks the whole control. One colour on
   * the root covers all three. The disabled rule is MUI's own and still wins on specificity.
   */
  MuiSlider: {
    styleOverrides: {
      root: {
        color: themeVariables.color.text.primary
      }
    }
  },
  /**
   * A dark track with a check on the thumb when on, a white track with an x on the thumb when off.
   * Every state is set explicitly: MUI colours the checked thumb and track from
   * `palette.primary.main`, which is white.
   */
  MuiSwitch: {
    styleOverrides: {
      root: {
        width: 39,
        height: 24,
        padding: 0,
        position: "relative",
        "& .MuiSwitch-switchBase": {
          padding: 3,
          position: "absolute",
          top: 0,
          left: 0,
          height: 24,
          zIndex: 1,
          "&.Mui-disabled": {
            // MUI fades the whole track, border included, to `opacity.switchTrackDisabled`.
            // The disabled state is carried by the thumb colour instead, so the track stays solid.
            "& + .MuiSwitch-track": {
              opacity: 1
            },
            "& .MuiSwitch-thumb": {
              backgroundColor: themeVariables.color.surface.tertiary,
              backgroundImage: "none"
            },
            "&.Mui-checked .MuiSwitch-thumb": {
              backgroundColor: themeVariables.color.surface.tertiary,
              backgroundImage: "none"
            }
          },
          "&.Mui-checked": {
            transform: "translateX(15px)",
            "& .MuiSwitch-thumb": {
              backgroundSize: "11px 11px",
              backgroundColor: themeVariables.color.surface.primary,
              backgroundImage: SWITCH_CHECK_ICON
            },
            "& + .MuiSwitch-track": {
              backgroundColor: themeVariables.color.surface.brand,
              border: "none",
              opacity: 1
            }
          }
        },
        "& .MuiSwitch-thumb": {
          width: 18,
          height: 18,
          borderRadius: "50%",
          backgroundColor: themeVariables.color.surface.brand,
          backgroundImage: SWITCH_CLOSE_ICON,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "8px 8px",
          boxShadow: "none"
        },
        "& .MuiSwitch-track": {
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: -1,
          backgroundColor: themeVariables.color.surface.primary,
          border: `1px solid ${themeVariables.color.border.secondary}`,
          borderRadius: 35,
          opacity: 1,
          boxSizing: "border-box"
        }
      }
    }
  },
  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        color: themeVariables.color.text.primary,
        "&.Mui-disabled": {
          color: themeVariables.color.text.disabled
        },
        // The -11px left margin offsets a control's own padding, which the themed switch has none of.
        "&:has(> .MuiSwitch-root)": {
          marginLeft: 0,
          gap: themeVariables.sizes.spacing["04"]
        }
      },
      label: {
        fontFamily: themeVariables.fonts.family.body,
        fontSize: themeVariables.fonts.size.sm,
        lineHeight: themeVariables.fonts.lineHeight["leading-5"],
        fontWeight: themeVariables.fonts.weight.normal,
        letterSpacing: themeVariables.fonts.letterSpacing.wide
      }
    }
  },
  /**
   * Outlined inputs, used by the TextField in Copy and CopyInline among others.
   * Same defaults as the date picker outline below: a translucent black at rest and
   * `palette.primary.main` (white, so invisible) when focused. Both are pinned to the hover
   * colour, which MUI already takes from `text.primary`, so focus only adds width.
   */
  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        borderColor: themeVariables.color.text.primary
      },
      root: {
        ["&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline"]: {
          borderColor: themeVariables.color.text.primary,
          borderWidth: themeVariables.sizes.borderWidth[2]
        }
      }
    }
  },
  /**
   * Date picker inputs (MUI X)
   * The outline defaults to a translucent black at rest and to `palette.primary.main`
   * (white, so invisible) when focused. Both are pinned to the same colour as the hover
   * state, which MUI X already takes from `text.primary`, so focus only adds width.
   */
  MuiPickersOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        borderColor: themeVariables.color.text.primary
      },
      root: {
        ["&.Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline"]: {
          borderColor: themeVariables.color.text.primary,
          borderWidth: themeVariables.sizes.borderWidth[2]
        }
      }
    }
  },
  /**
   * Select component
   */
  MuiSelect: {
    styleOverrides: {
      root: {
        width: "100%",
        minWidth: "250px",
        display: "flex",
        height: themeVariables.fonts.lineHeight["leading-10"],
        padding: themeVariables.sizes.spacing.none,
        alignItems: "center",
        gap: themeVariables.sizes.spacing["04"],
        border: `${themeVariables.sizes.borderWidth[1]} solid ${themeVariables.color.border.tertiary}`,
        backgroundColor: themeVariables.color.surface.primary,
        borderRadius: themeVariables.sizes.radius.sm,
        "&.MuiInputBase-root": {
          marginTop: themeVariables.sizes.spacing.none
        },
        "& .MuiSelect-select.MuiInputBase-input": {
          padding: `${themeVariables.sizes.spacing.none} ${themeVariables.sizes.spacing["07"]}`,
          border: "none"
        },
        "&:disabled, &.Mui-disabled": {
          backgroundColor: themeVariables.color.button.disabled,
          ":before": {
            borderBottomStyle: "none"
          }
        }
      },
      select: {
        color: themeVariables.color.text.primary,
        fontFamily: themeVariables.fonts.family.body,
        fontSize: themeVariables.fonts.size.sm,
        fontStyle: "normal",
        fontWeight: themeVariables.fonts.weight.medium,
        lineHeight: themeVariables.fonts.lineHeight["leading-5"],
        letterSpacing: themeVariables.fonts.letterSpacing.wide,
        "&:disabled, &.Mui-disabled": {
          backgroundColor: themeVariables.color.button.disabled
        }
      }
    }
  },
  MuiToggleButton: {
    defaultProps: {
      disableRipple: true
    },
    styleOverrides: {
      root: {
        flexGrow: 1,
        padding: `${themeVariables.sizes.spacing["05"]} ${themeVariables.sizes.spacing["07"]}`,
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "stretch",
        backgroundColor: themeVariables.color.surface.tertiary,
        color: themeVariables.color.text.tertiary,
        fontFamily: themeVariables.fonts.family.headline,
        fontSize: themeVariables.fonts.size.sm,
        fontStyle: "normal",
        fontWeight: themeVariables.fonts.weight.medium,
        lineHeight: themeVariables.fonts.lineHeight["leading-5"],
        letterSpacing: themeVariables.fonts.letterSpacing.wide,
        textTransform: "none",
        textAlign: "center",
        borderRadius: themeVariables.sizes.radius.sm,
        border: "none",
        "&.Mui-selected": {
          backgroundColor: themeVariables.color.surface.primary,
          borderColor: themeVariables.color.border.primary,
          color: themeVariables.color.text.primary,
          boxShadow: shadows[1]
        },
        "&:hover": {
          backgroundColor: themeVariables.color.surface.tertiary,
          color: themeVariables.color.text.primary
        },
        "&.Mui-selected:hover": {
          backgroundColor: themeVariables.color.surface.primary,
          color: themeVariables.color.text.primary
        }
      }
    }
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: {
        borderRadius: themeVariables.sizes.radius.md,
        padding: themeVariables.sizes.spacing["02"],
        backgroundColor: themeVariables.color.surface.tertiary,
        gap: themeVariables.sizes.spacing["02"],
        "& .MuiToggleButtonGroup-firstButton": {
          borderRadius: themeVariables.sizes.radius.md,
          margin: 0
        },
        "& .MuiToggleButtonGroup-lastButton": {
          borderRadius: themeVariables.sizes.radius.md,
          margin: 0
        },
        "& .MuiToggleButtonGroup-middleButton": {
          borderRadius: themeVariables.sizes.radius.md,
          margin: 0
        }
      }
    }
  },
  /**
   * Button component
   */
  MuiButtonBase: {
    styleOverrides: {
      root: {
        height: themeVariables.fonts.lineHeight["leading-10"],
        borderRadius: themeVariables.fonts.size.sm
      },
      input: {
        variants: [{
          props: {
            variant: "filled"
          },
          style: {}
        }, {
          props: {
            variant: "text-inverted"
          },
          style: {}
        }]
      }
    }
  },
  MuiButton: {
    defaultProps: {
      disableRipple: true,
      disableElevation: true,
      variant: "filled"
    },
    styleOverrides: {
      root: {
        // Base styles
        backgroundColor: themeVariables.color.button.surface,
        color: themeVariables.color.text.inverted,
        borderRadius: themeVariables.sizes.radius.md,
        padding: `${themeVariables.sizes.spacing["05"]} ${themeVariables.sizes.spacing["06"]}`,
        flexShrink: 0,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: themeVariables.sizes.spacing["03"],
        justifyContent: "center",
        textAlign: "center",
        fontFamily: themeVariables.fonts.family.headline,
        fontSize: themeVariables.fonts.size.sm,
        fontStyle: "normal",
        fontWeight: themeVariables.fonts.weight.medium,
        lineHeight: themeVariables.fonts.lineHeight["leading-5"],
        letterSpacing: themeVariables.fonts.letterSpacing.wide,
        border: "none",
        textTransform: "none",
        // Base interaction states
        "&:hover": {
          background: themeVariables.color.button.hover
        },
        "&:active": {
          backgroundColor: themeVariables.color.button.pressed,
          color: themeVariables.color.text.inverted
        },
        "&:focus, &.Mui-Focused, &.Mui-focusVisible": {
          backgroundColor: themeVariables.color.button.surface,
          outlineOffset: "2px",
          outline: `2px solid ${themeVariables.color.border.focus}`
        }
      }
    },
    variants: [{
      props: {
        variant: "filled"
      },
      style: {
        backgroundColor: themeVariables.color.button.surface,
        color: themeVariables.color.text.inverted,
        border: `1px solid ${themeVariables.color.button.border}`,
        "&:hover": {
          background: themeVariables.color.button.hover
        },
        "&:focus, &.Mui-Focused, &.Mui-focusVisible": {
          backgroundColor: themeVariables.color.button.surface,
          outlineOffset: "2px",
          outline: `2px solid ${themeVariables.color.border.focus}`
        },
        "&:disabled, &.Mui-disabled": {
          color: themeVariables.color.text.disabled,
          border: `1px solid ${themeVariables.color.button["secondary-border-disabled"]}`,
          backgroundColor: themeVariables.color.button.disabled
        }
      }
    }, {
      props: {
        variant: "outlined"
      },
      style: {
        backgroundColor: themeVariables.color.button["secondary-surface"],
        color: themeVariables.color.text.primary,
        border: `1px solid ${themeVariables.color.button.border}`,
        "&:hover": {
          border: `1px solid ${themeVariables.color.button["primary-hover"]}`,
          backgroundColor: themeVariables.color.button["secondary-surface"],
          outline: "none"
        },
        "&:focus, &.Mui-Focused, &.Mui-focusVisible": {
          color: themeVariables.color.text.primary,
          backgroundColor: themeVariables.color.button["secondary-surface"],
          outlineOffset: "2px",
          outline: `2px solid ${themeVariables.color.border.focus}`
        },
        "&:active": {
          color: themeVariables.color.text.primary,
          backgroundColor: themeVariables.color.button["secondary-border"],
          border: `1px solid ${themeVariables.color.button.border}`,
          outline: "none"
        },
        "&:disabled, &.Mui-disabled": {
          color: themeVariables.color.text.disabled,
          border: `1px solid ${themeVariables.color.button["secondary-border-disabled"]}`,
          backgroundColor: themeVariables.color.button["secondary-surface"]
        }
      }
    }, {
      props: {
        variant: "contained"
      },
      style: {
        backgroundColor: themeVariables.color.button.surface,
        color: themeVariables.color.text.inverted,
        border: `1px solid ${themeVariables.color.button.border}`,
        "&:hover": {
          background: themeVariables.color.button.hover
        },
        "&:focus, &.Mui-Focused, &.Mui-focusVisible": {
          backgroundColor: themeVariables.color.button.surface,
          outlineOffset: "2px",
          outline: `2px solid ${themeVariables.color.border.focus}`
        },
        "&:disabled, &.Mui-disabled": {
          color: themeVariables.color.text.disabled,
          border: `1px solid ${themeVariables.color.button["secondary-border-disabled"]}`,
          backgroundColor: themeVariables.color.button.disabled
        }
      }
    }, {
      props: {
        variant: "text"
      },
      style: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "transparent",
        height: "2.5rem",
        flexShrink: 0,
        color: themeVariables.color.text.primary,
        border: "2px solid transparent",
        borderRadius: themeVariables.sizes.radius.sm,
        gap: themeVariables.sizes.spacing["03"],
        padding: `${themeVariables.sizes.spacing["05"]} ${themeVariables.sizes.spacing["06"]}`,
        outline: "none",
        "&:hover": {
          backgroundColor: themeVariables.color.button["text-hover"],
          color: themeVariables.color.button["primary-surface"],
          border: "2px solid transparent",
          outline: "none"
        },
        "&:focus, &.Mui-Focused": {
          backgroundColor: "transparent",
          color: themeVariables.color.button["primary-surface"],
          border: `2px solid ${themeVariables.color.border.focus}`,
          outline: "none"
        },
        "&:active": {
          backgroundColor: themeVariables.color.button["text-pressed"],
          border: "2px solid transparent",
          outline: "none"
        },
        "&:disabled, &.Mui-disabled": {
          color: themeVariables.color.text.disabled,
          backgroundColor: "transparent",
          border: "2px solid transparent",
          outline: "none"
        }
      }
    }, {
      props: {
        variant: "text-inverted"
      },
      style: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "transparent",
        height: "2.5rem",
        flexShrink: 0,
        color: themeVariables.color.text.inverted,
        border: "none",
        borderRadius: themeVariables.sizes.radius.sm,
        gap: themeVariables.sizes.spacing["03"],
        padding: `${themeVariables.sizes.spacing["05"]} ${themeVariables.sizes.spacing["06"]}`,
        outline: "none",
        "&:hover": {
          backgroundColor: themeVariables.color.button["text-hover"],
          color: themeVariables.color.text.inverted,
          border: "none",
          outline: "none"
        },
        "&:focus, &.Mui-Focused": {
          backgroundColor: "transparent",
          color: themeVariables.color.text.inverted,
          border: "none",
          outline: "none"
        },
        "&:active": {
          backgroundColor: themeVariables.color.button["text-pressed"],
          border: "none",
          outline: "none"
        },
        "&:disabled, &.Mui-disabled": {
          color: themeVariables.color.text.disabled,
          backgroundColor: "transparent",
          border: "none",
          outline: "none"
        }
      }
    }]
  }
  // MuiInputAdornment: {
  //   styleOverrides: {
  //     root: {
  //       marginRight: '0rem',
  //     },
  //   },
  // },
};
export {
  inputs
};
